import axios from "axios";
import { incomingCallsAction } from "./incomingCallsAction";
import { showSuccessSnackbar } from "../../snackBar/snackBar.action";

const getAdminURL = (state) => state.environnment.environmentLists.adminBaseURL;

export const getAllIncomingCallsAsync = (searchBy, searchText, audioType = "all", fromDateFilter = "", toDateFilter = "", leadType = "all", callOutcomeStatus = "all", sortColumn = "createdOn", sortDirection = "desc") => {
  return async (dispatch, getState) => {
    try {
      const adminBaseURL = getAdminURL(getState());
      const { skip, limit, callOutcomeFilter: stateCallOutcome } = getState().incomingCalls;
      const { user } = getState().auth;
      dispatch(incomingCallsAction.getAllIncomingCallsStart());

      // Build query parameters
      // Use searchBy as column if provided, otherwise use sortColumn (default: createdOn)
      const column = searchBy || sortColumn;
      // Use sortDirection (default: desc)
      const dir = sortDirection || "desc";
      // Always use the latest callOutcomeFilter from Redux state
      const finalCallOutcome = stateCallOutcome || callOutcomeStatus || "all";

      let url = `${process.env.REACT_APP_HOST}${adminBaseURL}/incomingCalls/getAll?skip=${skip}&limit=${limit}&column=${column}&dir=${dir}&search=${encodeURIComponent(searchText || "")}&audio_type=${audioType}&call_outcome_status=${finalCallOutcome}`;

      // Add from date filter if provided (format: DD/MM/YYYY)
      if (fromDateFilter) {
        url += `&from_date=${encodeURIComponent(fromDateFilter)}`;
      }

      // Add to date filter if provided (format: DD/MM/YYYY)
      if (toDateFilter) {
        url += `&to_date=${encodeURIComponent(toDateFilter)}`;
      }

      // Add lead type filter if provided and not "all"
      if (leadType && leadType !== "all") {
        url += `&lead_type=${encodeURIComponent(leadType)}`;
      }

      // Frontend filtering for list view:
      // Super Admin (roleId == 1): Can view all leads (no created_by filter)
      // Sales Team: Can only view their own leads (filtered by created_by = user._id)
      // eslint-disable-next-line eqeqeq
      if (user && user._id && user.roleId != 1) {
        url += `&created_by=${user._id}`;
      }

      const { data } = await axios({
        method: "GET",
        url,
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (data?.code === 200) {
        // Map API response { total, data: [...] } to { records: [...], recordsTotal }
        const apiData = data.data || {};
        const mapped = {
          records: apiData.data || apiData.records || [],
          recordsTotal: apiData.total || apiData.recordsTotal || 0,
        };
        dispatch(incomingCallsAction.getAllIncomingCallsSuccess(mapped));
      } else {
        dispatch(incomingCallsAction.getAllIncomingCallsError());
        dispatch(
          showSuccessSnackbar(
            "error",
            data?.message || "Failed to fetch incoming calls",
            3000
          )
        );
      }
    } catch (error) {
      console.error("Failed to fetch incoming calls", error);
      dispatch(incomingCallsAction.getAllIncomingCallsError());
      dispatch(showSuccessSnackbar("error", "Error while fetching incoming calls", 3000));
    }
  };
};

// View single lead - Backend handles permission check:
// Super Admin (roleId == 1): Can view any lead
// Sales Team: Can only view their own leads (created_by = req._id)
export const getIncomingCallAsync = (id) => {
  return async (dispatch, getState) => {
    try {
      const adminBaseURL = getAdminURL(getState());
      dispatch(incomingCallsAction.getIncomingCallStart());
      const { data } = await axios({
        method: "GET",
        url: `${process.env.REACT_APP_HOST}${adminBaseURL}/incomingCalls/getCallDetails/${id}`,
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (data?.code === 200) {
        const payload = data.data || {};
        // Extract lead from the response structure: { lead: {...}, call_ai: [...] }
        const leadData = payload.lead || payload;
        dispatch(incomingCallsAction.getIncomingCallSuccess(leadData));
      } else {
        dispatch(incomingCallsAction.getIncomingCallError());
        dispatch(
          showSuccessSnackbar(
            "error",
            data?.message || "Failed to fetch cold lead",
            3000
          )
        );
      }
    } catch (error) {
      console.error("Failed to fetch cold lead", error);
      dispatch(incomingCallsAction.getIncomingCallError());
      dispatch(showSuccessSnackbar("error", "Error while fetching cold lead", 3000));
    }
  };
};

// Delete single lead - Backend handles permission check:
// Super Admin (roleId == 1): Can delete any lead
// Sales Team: Can only delete their own leads (created_by = req._id)
export const deleteIncomingCallAsync = (id) => {
  return async (dispatch, getState) => {
    try {
      const adminBaseURL = getAdminURL(getState());
      dispatch(incomingCallsAction.getIncomingCallStart());
      const { data } = await axios({
        method: "POST",
        url: `${process.env.REACT_APP_HOST}${adminBaseURL}/incomingCalls/removeMultiIncomingCalls`,
        headers: {
          "Content-Type": "application/json",
        },
        data: {
          data: [id],
        },
      });
      if (data?.code === 200) {
        dispatch(
          showSuccessSnackbar("success", data?.message || "Lead deleted successfully", 3000)
        );
        dispatch(incomingCallsAction.getIncomingCallSuccess({}));
      } else {
        dispatch(incomingCallsAction.getIncomingCallError());
        dispatch(
          showSuccessSnackbar(
            "error",
            data?.message || "Failed to delete lead",
            3000
          )
        );
      }
    } catch (error) {
      console.error("Failed to delete cold lead", error);
      dispatch(incomingCallsAction.getIncomingCallError());
      dispatch(showSuccessSnackbar("error", "Error while deleting lead", 3000));
    }
  };
};

// Delete multiple leads - Backend handles permission check:
// Super Admin (roleId == 1): Can delete any leads
// Sales Team: Can only delete their own leads (created_by = req._id)
export const deleteSelectedIncomingCallsAsync = (ids) => {
  return async (dispatch, getState) => {
    try {
      const adminBaseURL = getAdminURL(getState());
      dispatch(incomingCallsAction.getIncomingCallStart());
      const { data } = await axios({
        method: "POST",
        url: `${process.env.REACT_APP_HOST}${adminBaseURL}/incomingCalls/deletedSelectedIncomingCalls`,
        headers: {
          "Content-Type": "application/json",
        },
        data: {
          ids,
        },
      });
      if (data?.code === 200) {
        dispatch(
          showSuccessSnackbar(
            "success",
            data?.message || "Selected leads deleted successfully",
            3000
          )
        );
        dispatch(incomingCallsAction.getIncomingCallSuccess({}));
      } else {
        dispatch(incomingCallsAction.getIncomingCallError());
        dispatch(
          showSuccessSnackbar(
            "error",
            data?.message || "Failed to delete selected leads",
            3000
          )
        );
      }
    } catch (error) {
      console.error("Failed to delete selected incoming calls", error);
      dispatch(incomingCallsAction.getIncomingCallError());
      dispatch(showSuccessSnackbar("error", "Error while deleting selected leads", 3000));
    }
  };
};

export const importIncomingCallsAsync = (file) => {
  return async (dispatch, getState) => {
    if (!file) {
      dispatch(showSuccessSnackbar("error", "Please select a file to import.", 3000));
      return { success: false, error: "No file selected" };
    }

    try {
      const adminBaseURL = getAdminURL(getState());
      const formData = new FormData();
      formData.append("file", file);

      const { data } = await axios({
        method: "POST",
        url: `${process.env.REACT_APP_HOST}${adminBaseURL}/incomingCalls/import`,
        headers: {
          "Content-Type": "multipart/form-data",
        },
        data: formData,
      });

      if (data?.code === 200 && data?.status === true) {
        const importData = data.data || {};
        //const message = `${data.message || "incoming calls imported successfully."} Processed: ${importData.processed || 0}, Inserted: ${importData.inserted || 0}, Updated: ${importData.updated || 0}, Skipped: ${importData.skipped || 0}. AI Calls Initiated: ${importData.aiCalls?.initiated || 0}, Failed: ${importData.aiCalls?.failed || 0}, Skipped: ${importData.aiCalls?.skipped || 0}.`;
        const message = `${data.message || "incoming calls imported successfully."}`;
        dispatch(showSuccessSnackbar("success", message, 5000));
        return { success: true, data: importData };
      } else {
        dispatch(
          showSuccessSnackbar(
            "error",
            data?.message || "Failed to import incoming calls",
            3000
          )
        );
        return { success: false, error: data?.message || "Failed to import incoming calls" };
      }
    } catch (error) {
      console.error("Failed to import incoming calls", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Error while importing incoming calls";
      dispatch(showSuccessSnackbar("error", errorMessage, 3000));
      return { success: false, error: errorMessage };
    }
  };
};

// Initiate call for a lead - Backend handles permission check:
// Super Admin (roleId == 1): Can call any lead
// Sales Team: Can only call their own leads (created_by = req._id)
export const startIncomingCallCallAsync = (leadId) => {
  return async (dispatch, getState) => {
    if (!leadId) {
      dispatch(showSuccessSnackbar("error", "Lead ID is required to start a call.", 3000));
      return { success: false, error: "Lead ID is required" };
    }

    try {
      const adminBaseURL = getAdminURL(getState());
      const { data } = await axios({
        method: "POST",
        url: `${process.env.REACT_APP_HOST}${adminBaseURL}/incomingCalls/initiateCall/${leadId}`,
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (data?.code === 200 && data?.status === true) {
        dispatch(
          showSuccessSnackbar(
            "success",
            data?.message || "Call initiated successfully",
            3000
          )
        );
        return { success: true, data: data.data };
      } else {
        dispatch(
          showSuccessSnackbar(
            "error",
            data?.message || "Failed to initiate call",
            3000
          )
        );
        return { success: false, error: data?.message || "Failed to initiate call" };
      }
    } catch (error) {
      console.error("Failed to initiate call", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Error while initiating call";
      dispatch(showSuccessSnackbar("error", errorMessage, 3000));
      return { success: false, error: errorMessage };
    }
  };
};

// Bulk call multiple leads - Backend handles permission check:
// Super Admin (roleId == 1): Can call any leads
// Sales Team: Can only call their own leads (created_by = req._id)
export const bulkCallIncomingCallsAsync = (leadIds) => {
  return async (dispatch, getState) => {
    if (!leadIds || leadIds.length === 0) {
      dispatch(showSuccessSnackbar("error", "Please select at least one lead to call.", 3000));
      return { success: false, error: "No leads selected" };
    }

    try {
      const adminBaseURL = getAdminURL(getState());
      const { data } = await axios({
        method: "POST",
        url: `${process.env.REACT_APP_HOST}${adminBaseURL}/incomingCalls/bulkCall`,
        headers: {
          "Content-Type": "application/json",
        },
        data: {
          leadIds,
        },
      });

      if (data?.code === 200 && data?.status === true) {
        dispatch(
          showSuccessSnackbar(
            "success",
            data?.message || "Bulk calls initiated successfully",
            5000
          )
        );
        return { success: true, data: data.data };
      } else {
        dispatch(
          showSuccessSnackbar(
            "error",
            data?.message || "Failed to initiate bulk calls",
            3000
          )
        );
        return { success: false, error: data?.message || "Failed to initiate bulk calls" };
      }
    } catch (error) {
      console.error("Failed to initiate bulk calls", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Error while initiating bulk calls";
      dispatch(showSuccessSnackbar("error", errorMessage, 3000));
      return { success: false, error: errorMessage };
    }
  };
};

export const downloadSampleExcelAsync = () => {
  return async (dispatch, getState) => {
    try {
      const adminBaseURL = getAdminURL(getState());
      const response = await axios({
        method: "GET",
        url: `${process.env.REACT_APP_HOST}${adminBaseURL}/incomingCalls/downloadSample`,
        responseType: "blob",
        headers: {
          "Content-Type": "application/json",
        },
      });

      // Create a blob from the response
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Create a temporary URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "incoming_calls_sample.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      dispatch(showSuccessSnackbar("success", "Sample file downloaded successfully", 3000));
      return { success: true };
    } catch (error) {
      console.error("Failed to download sample file", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Error while downloading sample file";
      dispatch(showSuccessSnackbar("error", errorMessage, 3000));
      return { success: false, error: errorMessage };
    }
  };
};

// Update cold lead - Backend handles permission check:
// Super Admin (roleId == 1): Can update any lead
// Sales Team: Can only update their own leads (created_by = req._id)
export const updateIncomingCallAsync = (leadId, updateData) => {
  return async (dispatch, getState) => {
    if (!leadId) {
      dispatch(showSuccessSnackbar("error", "Lead ID is required to update.", 3000));
      return { success: false, error: "Lead ID is required" };
    }

    try {
      const adminBaseURL = getAdminURL(getState());
      const { data } = await axios({
        method: "PUT",
        url: `${process.env.REACT_APP_HOST}${adminBaseURL}/incomingCalls/update/${leadId}`,
        headers: {
          "Content-Type": "application/json",
        },
        data: updateData,
      });

      if (data?.code === 200 && data?.status === true) {
        dispatch(
          showSuccessSnackbar(
            "success",
            data?.message || "Lead updated successfully",
            3000
          )
        );
        // Update the selected cold lead in Redux state
        if (data.data) {
          dispatch(incomingCallsAction.getIncomingCallSuccess(data.data));
        }
        return { success: true, data: data.data };
      } else {
        // Check if it's a duplicate contact number error
        const isDuplicate = data?.message?.toLowerCase().includes("already exists") ||
          data?.message?.toLowerCase().includes("duplicate");
        dispatch(
          showSuccessSnackbar(
            "error",
            data?.message || "Failed to update lead",
            3000
          )
        );
        return {
          success: false,
          error: data?.message || "Failed to update lead",
          isDuplicate
        };
      }
    } catch (error) {
      console.error("Failed to update cold lead", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Error while updating cold lead";
      const isDuplicate = errorMessage?.toLowerCase().includes("already exists") ||
        errorMessage?.toLowerCase().includes("duplicate");
      dispatch(showSuccessSnackbar("error", errorMessage, 3000));
      return {
        success: false,
        error: errorMessage,
        isDuplicate
      };
    }
  };
};

// Update leadType - Backend handles permission check:
// Super Admin (roleId == 1): Can update any lead
// Sales Team: Can only update their own leads (created_by = req._id)
export const updateLeadTypeAsync = (leadId, leadType) => {
  return async (dispatch, getState) => {
    if (!leadId) {
      dispatch(showSuccessSnackbar("error", "Lead ID is required to update lead type.", 3000));
      return { success: false, error: "Lead ID is required" };
    }

    try {
      const adminBaseURL = getAdminURL(getState());
      const { data } = await axios({
        method: "PUT",
        url: `${process.env.REACT_APP_HOST}${adminBaseURL}/incomingCalls/updateLeadType/${leadId}`,
        headers: {
          "Content-Type": "application/json",
        },
        data: { leadType },
      });

      if (data?.code === 200 && data?.status === true) {
        dispatch(
          showSuccessSnackbar(
            "success",
            data?.message || "Lead type updated successfully",
            3000
          )
        );
        return { success: true, data: data.data };
      } else {
        dispatch(
          showSuccessSnackbar(
            "error",
            data?.message || "Failed to update lead type",
            3000
          )
        );
        return {
          success: false,
          error: data?.message || "Failed to update lead type"
        };
      }
    } catch (error) {
      console.error("Failed to update lead type", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Error while updating lead type";
      dispatch(showSuccessSnackbar("error", errorMessage, 3000));
      return {
        success: false,
        error: errorMessage
      };
    }
  };
};

// Check if contact number already exists
// Backend handles permission check:
// Super Admin (roleId == 1): Can check any contact number
// Sales Team: Can only check contact numbers for their own leads
export const checkContactNumberExistsAsync = (contactNumber, excludeId = null) => {
  return async (dispatch, getState) => {
    if (!contactNumber || contactNumber.trim() === "") {
      return { success: true, exists: false };
    }

    try {
      const adminBaseURL = getAdminURL(getState());
      let url = `${process.env.REACT_APP_HOST}${adminBaseURL}/incomingCalls/checkContactNumber`;

      // Add excludeId as query parameter if provided
      if (excludeId) {
        url += `?excludeId=${excludeId}`;
      }

      const { data } = await axios({
        method: "POST",
        url,
        headers: {
          "Content-Type": "application/json",
        },
        data: { contactNumber: contactNumber.trim() },
      });

      if (data?.code === 200) {
        const exists = data.data?.exists || false;
        return {
          success: true,
          exists,
          data: data.data
        };
      } else {
        return {
          success: false,
          exists: false,
          error: data?.message || "Failed to check contact number"
        };
      }
    } catch (error) {
      console.error("Failed to check contact number", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Error while checking contact number";
      return {
        success: false,
        exists: false,
        error: errorMessage
      };
    }
  };
};

